import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { companyApi } from '@/api';
import { GreetingBanner, RailCard, StatRing, todayLine } from '@/components/shell';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  EntityCell,
  ErrorState,
  Icon,
  LinkButton,
  LoadingState,
  ProgressBar,
} from '@/components/ui';
import { RecruitmentPerformance } from '@/features/analytics/RecruitmentPerformance';
import { RecruitmentSummary } from '@/features/analytics/RecruitmentSummary';
import { errorMessage } from '@/hooks/useApiError';
import { VerifyToContinueButton } from '@/features/verification/CompanyVerificationGate';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';

const VERIFICATION_TONES = {
  VERIFIED: 'success',
  PENDING: 'warning',
  REJECTED: 'danger',
  UNVERIFIED: 'neutral',
};

export function CompanyDashboardPage() {
  const {
    data: company,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['company-profile'],
    queryFn: companyApi.getProfile,
  });

  const { data: completion } = useQuery({
    queryKey: ['company-completion'],
    queryFn: companyApi.getCompletion,
  });

  const { data: members } = useQuery({
    queryKey: ['company-members', 1],
    queryFn: () => companyApi.listMembers({ page: 1, page_size: 20 }),
  });

  const { can } = usePermissions();

  if (isPending) return <LoadingState label="Loading your workspace…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const sections = completion?.sections ?? [];
  const nextStep = sections.find((section) => !section.complete);
  const completed = sections.filter((section) => section.complete).length;
  const percentage = completion?.percentage ?? company.profile_completion;
  const memberCount = members?.meta?.total_items ?? 0;
  const isVerified = company.verification_status === 'VERIFIED';
  // Only point a member at what they are allowed to open.
  const canEditProfile = can(PERMISSIONS.COMPANY_PROFILE_UPDATE);
  const allowed = (links) => links.filter((link) => !link.permission || can(link.permission));

  return (
    <div>
      <GreetingBanner
        name={company.name}
        subtitle={todayLine()}
        action={
          can(PERMISSIONS.INTERNSHIP_CREATE) &&
          (isVerified ? (
            <LinkButton to={paths.company.internshipNew} variant="light">
              Post an internship
            </LinkButton>
          ) : (
            <VerifyToContinueButton variant="light" />
          ))
        }
      />

      {/* Verification is prompted by the workspace banner and the guide. */}
      {can(PERMISSIONS.ANALYTICS_READ) && (
        <>
          <RecruitmentSummary />
          <RecruitmentPerformance />
        </>
      )}

      <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-6">
          {canEditProfile && (
            <Card>
              <CardHeader
                title="Finish your profile"
                description="A complete profile is required before verification."
                action={
                  <LinkButton to={paths.company.profile} variant="secondary" size="sm">
                    Edit profile
                  </LinkButton>
                }
              />
              <ProgressBar value={percentage} label="Complete" />
              {nextStep ? (
                <ul className="mt-4 space-y-2">
                  {sections
                    .filter((section) => !section.complete)
                    .map((section) => (
                      <li key={section.key} className="flex items-start gap-2.5 text-sm">
                        <span
                          className="mt-1.5 size-1.5 shrink-0 bg-warning-500/80 rounded-full"
                          aria-hidden="true"
                        />
                        <span className="min-w-0">
                          <span className="text-ink font-medium">{section.label}</span>
                          <span className="text-ink-muted block text-xs">{section.hint}</span>
                        </span>
                      </li>
                    ))}
                </ul>
              ) : (
                <div className="mt-4">
                  <EmptyState
                    compact
                    icon={<Icon name="check" size={16} />}
                    title="Everything is filled in. Nothing is blocking verification."
                  />
                </div>
              )}
            </Card>
          )}

          <Card>
            <CardHeader
              title="Your team"
              description="People with access to this workspace."
              action={
                <LinkButton to={paths.company.team} variant="secondary" size="sm">
                  {can(PERMISSIONS.COMPANY_MEMBERS_MANAGE) ? 'Manage team' : 'View team'}
                </LinkButton>
              }
            />
            {memberCount === 0 ? (
              <EmptyState
                compact
                icon={<Icon name="users" size={16} />}
                title="Nobody else has been invited yet."
              />
            ) : (
              <ul className="divide-line divide-y">
                {(members?.data ?? []).slice(0, 5).map((member) => (
                  <li key={member.id} className="flex items-center justify-between gap-4 py-3">
                    <EntityCell name={member.full_name} subtitle={member.email} />
                    <Badge dot tone={member.status === 'ACTIVE' ? 'success' : 'warning'}>
                      {member.status}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <aside className="space-y-6">
          <RailCard
            icon="company"
            title="Workspace health"
            subtitle="Where the account stands"
            links={allowed([
              {
                to: paths.company.profile,
                label: 'Edit profile',
                permission: PERMISSIONS.COMPANY_PROFILE_UPDATE,
              },
              { to: paths.company.team, label: 'Team' },
            ])}
          >
            <div className="grid grid-cols-3 gap-3">
              <StatRing value={percentage} max={100} label="Profile" unit="%" />
              <StatRing
                value={completed}
                max={sections.length || 1}
                label="Sections"
                unit={sections.length ? `of ${sections.length}` : null}
              />
              <StatRing value={memberCount} label="Team" tone="success" />
            </div>
          </RailCard>

          <RailCard
            icon="verification"
            title="Verification"
            subtitle={isVerified ? 'Approved' : 'Adds trust for applicants'}
            links={
              isVerified || !canEditProfile
                ? undefined
                : [{ to: paths.company.verification, label: 'Start now' }]
            }
          >
            <div className="flex items-center justify-between gap-3">
              <Badge dot tone={VERIFICATION_TONES[company.verification_status] ?? 'neutral'}>
                {company.verification_status}
              </Badge>
              {nextStep && !isVerified && (
                <span className="text-ink-muted truncate text-xs">Next: {nextStep.label}</span>
              )}
            </div>
          </RailCard>

          <RailCard
            icon="internship"
            title="Keep hiring moving"
            subtitle="The usual next steps"
            links={allowed([
              {
                to: paths.company.applications,
                label: 'Applications',
                permission: PERMISSIONS.APPLICATION_READ,
              },
              {
                to: paths.company.candidates,
                label: 'Find candidates',
                permission: PERMISSIONS.CANDIDATE_READ,
              },
            ])}
          >
            <ul className="space-y-1.5">
              {allowed([
                {
                  to: paths.company.internships,
                  label: 'Review your open internships',
                  permission: PERMISSIONS.INTERNSHIP_READ,
                },
                {
                  to: paths.company.interviews,
                  label: 'Check upcoming interviews',
                  permission: PERMISSIONS.INTERVIEW_CREATE,
                },
                {
                  to: paths.company.offers,
                  label: 'Follow up on offers',
                  permission: PERMISSIONS.OFFER_CREATE,
                },
              ]).map((row) => (
                <li key={row.to}>
                  <Link
                    to={row.to}
                    className="text-ink-muted hover:text-brand-700 flex items-center gap-2 text-xs transition"
                  >
                    <span className="bg-brand-300 size-1.5 rounded-full" aria-hidden="true" />
                    {row.label}
                  </Link>
                </li>
              ))}
            </ul>
          </RailCard>
        </aside>
      </div>
    </div>
  );
}
